'use client'

import * as React from 'react'

import { cn } from '@/lib/utils'

/**
 * SSR-safe `prefers-reduced-motion`. Reads synchronously on the client so a
 * reduced-motion user never sees a frame of motion; the value is never rendered
 * into markup, so there is no hydration-mismatch risk.
 */
function useReducedMotion(): boolean {
  const [reduced, setReduced] = React.useState(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
  React.useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    setReduced(mq.matches)
    const onChange = (e: MediaQueryListEvent) => setReduced(e.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])
  return reduced
}

/**
 * Returns whether the referenced element is currently worth animating — i.e.
 * on-screen AND the tab is visible. Use it to pause per-frame work, autoplay,
 * or streaming when the component scrolls away or the tab is backgrounded.
 */
function useVisibilityPause<T extends Element>(
  ref: React.RefObject<T | null>,
  { threshold = 0.1 }: { threshold?: number } = {}
): boolean {
  const [onScreen, setOnScreen] = React.useState(true)
  const [tabVisible, setTabVisible] = React.useState(true)

  React.useEffect(() => {
    const el = ref.current
    if (!el || typeof IntersectionObserver === 'undefined') return
    const io = new IntersectionObserver(
      (entries) => setOnScreen(entries.some((e) => e.isIntersecting)),
      { threshold }
    )
    io.observe(el)
    return () => io.disconnect()
  }, [ref, threshold])

  React.useEffect(() => {
    const onVis = () => setTabVisible(document.visibilityState !== 'hidden')
    onVis()
    document.addEventListener('visibilitychange', onVis)
    return () => document.removeEventListener('visibilitychange', onVis)
  }, [])

  return onScreen && tabVisible
}

/* -------------------------------------------------------------------------- */
/* Types                                                                      */
/* -------------------------------------------------------------------------- */

export interface CopperplateFocalPoint {
  /** 0–1 horizontal position of a "light source" driving the tonal field. */
  x: number
  /** 0–1 vertical position of a "light source" driving the tonal field. */
  y: number
}

export interface CopperplateSafeArea {
  /** 0–1 left edge of the readable safe area. */
  x: number
  /** 0–1 top edge of the readable safe area. */
  y: number
  /** 0–1 width of the readable safe area. */
  w: number
  /** 0–1 height of the readable safe area. */
  h: number
}

export interface CopperplateHatchProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Hatch-stroke density multiplier (smaller pitch = more strokes). ~0.4–1.6. */
  density?: number
  /**
   * How much of the plate carries ink independently of the traveling light.
   * 1 is upstream behaviour, where the two finer layers only ink near the focal
   * point and the rest of the plate stays bare. Above 1 lowers the tonal gate so
   * the hatch reads across the whole surface. ~1–2.2.
   */
  coverage?: number
  /** Overall ink/glint contrast of the plate (0–1.4). */
  intensity?: number
  /** Light-wave travel speed multiplier. 0 (or reduced motion) freezes on a rich still frame. */
  speed?: number
  /**
   * One or two "light source" points. The first drives the traveling radial light
   * (follows the pointer when `interactive`); the second, if given, is a static
   * secondary bright zone in the tonal field.
   */
  focalPoint?: CopperplateFocalPoint | CopperplateFocalPoint[]
  /** Region where the plate dims toward the ground so foreground text stays readable. */
  safeArea?: CopperplateSafeArea
  /** Overrides the metallic glint / focal light gold. Accepts a plain `#hex` or
   *  `rgb()`/`rgba()` string; omit to use the exact Motion Lab gold (`#eab365`). */
  accent?: string
  /** Deterministic seed for the hatch field layout (SSR-stable). */
  seed?: number
  /** Pause the loop when scrolled offscreen or the tab is hidden. */
  pauseWhenHidden?: boolean
  /** The focal light follows the pointer (never the sole effect). */
  interactive?: boolean
  /** Force the static, motion-free variant regardless of system preference. */
  reducedMotion?: boolean
}

/* -------------------------------------------------------------------------- */
/* Deterministic helpers                                                     */
/* -------------------------------------------------------------------------- */

const TAU = Math.PI * 2
/** Rich still frame — the light mid-sweep, strokes glinting (also the reduced-motion frame). */
const STILL_SECONDS = 7.3

/** Fixed engraver's cross-hatch directions (degrees) — never randomized, never move. */
const ANGLES = [32, -32, 90] as const
const ANGLE_RAD = ANGLES.map((a) => (a * Math.PI) / 180)
/** Stroke budget, summed across layers, bounding per-frame cost on large
    canvases. Raised from upstream's 700/500/300, which spends out long before a
    wide hero is covered. The split still matters: it is the per-layer share of
    the grid, so the coarse angle stays denser than the fine ones. */
const LAYER_CAP = [1500, 1100, 750] as const

const DEFAULT_SAFE_AREA: CopperplateSafeArea = {
  x: 0.04,
  y: 0.12,
  w: 0.56,
  h: 0.76,
}
const DEFAULT_FOCAL: CopperplateFocalPoint = { x: 0.62, y: 0.42 }

/** mulberry32 — no Math.random / Date.now at render or module scope (SSR-stable). */
function makeRng(seed: number) {
  let a = seed >>> 0
  return () => {
    a = (a + 0x6d2b79f5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

const clamp = (n: number, lo: number, hi: number) =>
  Math.min(hi, Math.max(lo, n))

/* -------------------------------------------------------------------------- */
/* Canvas colors — read from the host theme (see note below).                 */
/* -------------------------------------------------------------------------- */

type RGB = [number, number, number]

/** candle-gold glint / focal light — overridable via `accent`. */
const GOLD: RGB = [234, 179, 101] // #eab365
/** Fallbacks only. Used if the host page has no theme tokens to read. */
const FALLBACK_GROUND: RGB = [10, 11, 16] // #0a0b10
const FALLBACK_INK: RGB = [158, 163, 196] // #9ea3c4

/* Upstream ships this as a committed-dark component with a fixed `#0a0b10`
   ground, which reads as a foreign black slab on a themed page. It is made
   theme-aware here: the ground comes from `--background` and the ink from
   `--muted-foreground`, both re-read when the theme flips. The glint stays a
   real accent colour, since that is the whole effect. */

/** `240 5.9% 10%` (shadcn token form) → rgb triplet. */
function hslTripletToRgb(triplet: string, fallback: RGB): RGB {
  const m = triplet.trim().match(/^([\d.]+)\s+([\d.]+)%\s+([\d.]+)%$/)
  if (!m) return fallback
  const h = parseFloat(m[1]!) / 360
  const s = parseFloat(m[2]!) / 100
  const l = parseFloat(m[3]!) / 100
  if ([h, s, l].some(Number.isNaN)) return fallback
  if (s === 0) {
    const v = Math.round(l * 255)
    return [v, v, v]
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s
  const p = 2 * l - q
  const hue = (t: number) => {
    let x = t
    if (x < 0) x += 1
    if (x > 1) x -= 1
    if (x < 1 / 6) return p + (q - p) * 6 * x
    if (x < 1 / 2) return q
    if (x < 2 / 3) return p + (q - p) * (2 / 3 - x) * 6
    return p
  }
  return [
    Math.round(hue(h + 1 / 3) * 255),
    Math.round(hue(h) * 255),
    Math.round(hue(h - 1 / 3) * 255),
  ]
}

/** Read a shadcn HSL custom property off an element. */
function readToken(el: Element, name: string, fallback: RGB): RGB {
  if (typeof window === 'undefined') return fallback
  const raw = getComputedStyle(el).getPropertyValue(name)
  if (!raw) return fallback
  return hslTripletToRgb(raw, fallback)
}

/** Parse a plain CSS color (hex, or `rgb()`/`rgba()`) into an RGB triplet. */
function parseAccentColor(value: string | undefined, fallback: RGB): RGB {
  if (!value) return fallback
  const c = value.trim()
  if (c.startsWith('#')) {
    let h = c.slice(1)
    if (h.length === 3)
      h = h
        .split('')
        .map((d) => d + d)
        .join('')
    if (h.length !== 6) return fallback
    const n = parseInt(h, 16)
    if (Number.isNaN(n)) return fallback
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
  }
  const m = c.match(/^rgba?\(([^)]+)\)$/)
  if (m) {
    const parts = m[1]!
      .split(/[\s,/]+/)
      .filter(Boolean)
      .slice(0, 3)
      .map(Number)
    if (parts.length === 3 && parts.every((p) => !Number.isNaN(p)))
      return [parts[0]!, parts[1]!, parts[2]!]
  }
  return fallback
}

const rgba = (c: RGB, a: number) =>
  `rgba(${c[0] | 0}, ${c[1] | 0}, ${c[2] | 0}, ${clamp(a, 0, 1)})`

/* -------------------------------------------------------------------------- */
/* Deterministic field — rebuilt on mount + resize (real container pixels)    */
/* -------------------------------------------------------------------------- */

interface CopperStroke {
  x: number
  y: number
  li: number
  base: number
  th: number
}

interface CopperState {
  strokes: CopperStroke[]
  /** Half-length of each hatch stroke (derived from the density-scaled step). */
  half: number
  /** Current (lerped) light position. */
  fx: number
  fy: number
  /** Target light position (pointer, or the static default). */
  tx: number
  ty: number
}

interface FocalPx {
  x: number
  y: number
  amp: number
  sigma: number
}

function normalizeFocals(
  focalPoint: CopperplateHatchProps['focalPoint'],
  W: number,
  H: number
): FocalPx[] {
  const list = Array.isArray(focalPoint)
    ? focalPoint
    : [focalPoint ?? DEFAULT_FOCAL]
  return list.slice(0, 2).map((f, i) => ({
    x: f.x * W,
    y: f.y * H,
    amp: 1 - i * 0.28,
    sigma: 230 - i * 40,
  }))
}

function buildCopperState(
  seed: number,
  density: number,
  focalPoint: CopperplateHatchProps['focalPoint'],
  W: number,
  H: number,
  coverage: number
): CopperState {
  const rng = makeRng((seed >>> 0) * 2654435761 + 31)
  const wanted = clamp(24 / Math.max(0.4, density), 14, 40)

  /* One grid for all three layers — this is what makes the mark.
     The three fixed angles (32°, -32°, 90°) only read as an engraver's asterisk
     because they land on the SAME node; give each layer its own pitch and the
     strokes scatter into unrelated dashes instead of stars.

     So the pitch is sized once, from the COMBINED budget, coarsening on big
     canvases rather than truncating. Upstream instead walks rows top-down per
     layer and breaks the moment that layer's cap is reached, which leaves the
     lower part of a wide, short canvas with no strokes at all. */
  const totalCap = LAYER_CAP.reduce((a, b) => a + b, 0)
  const step = Math.max(wanted, Math.sqrt((ANGLES.length * W * H) / totalCap))
  const half = step * 0.46

  /* Inset the grid by a stroke's own reach (plus line-width slack) and centre
     what fits, so every asterisk renders whole — never sliced off at a canvas
     edge. Rows/cols can come out non-positive on a degenerate canvas; the
     loops then simply emit no strokes. */
  const margin = half + 2
  const cols = Math.floor((W - margin * 2) / step) + 1
  const rows = Math.floor((H - margin * 2) / step) + 1
  const x0 = (W - (cols - 1) * step) / 2
  const y0 = (H - (rows - 1) * step) / 2

  const harm = [
    { f: 0.006 + rng() * 0.004, a: 0.22, p: rng() * TAU },
    { f: 0.009 + rng() * 0.004, a: 0.16, p: rng() * TAU },
  ]
  const focals = normalizeFocals(focalPoint, W, H)
  const secondary = focals[1]

  const strokes: CopperStroke[] = []
  for (let li = 0; li < 3; li++) {
    /* Upstream compares this against a tonal value whose non-focal part peaks at
       0.38, so layers 1–2 (0.64 / 0.94) only ever ink within a few hundred px of
       the light and the rest of the plate stays blank. `coverage` scales the
       gate so ink can carry across the whole plate; 1 keeps upstream behaviour. */
    const th = (0.34 + li * 0.3) / Math.max(0.1, coverage)
    for (let r = 0; r < rows; r++) {
      const y = y0 + r * step
      const rowOffset = (r % 2) * step * 0.5
      for (let c = 0; c < cols; c++) {
        const x = x0 + rowOffset + c * step
        // A staggered row's last node can poke past the inset — drop it whole.
        if (x > W - margin) break
        let v = 0
        for (const h of harm)
          v += h.a * (0.5 + 0.5 * Math.sin(x * h.f + y * h.f * 0.7 + h.p))
        if (secondary) {
          const dx = x - secondary.x
          const dy = y - secondary.y
          v +=
            secondary.amp *
            0.9 *
            Math.exp(
              -(dx * dx + dy * dy) / (2 * secondary.sigma * secondary.sigma)
            )
        }
        strokes.push({ x, y, li, base: v, th })
      }
    }
  }

  const primary = focals[0] ?? { x: W * 0.62, y: H * 0.42, amp: 1, sigma: 230 }
  return {
    strokes,
    half,
    fx: primary.x,
    fy: primary.y,
    tx: primary.x,
    ty: primary.y,
  }
}

/**
 * Paints the readable quiet zone — always the LAST canvas op, shaped as an
 * ellipse anchored to `safeArea` so copy stays legible at any container size.
 */
function paintQuietZone(
  ctx: CanvasRenderingContext2D,
  W: number,
  H: number,
  sa: { x: number; y: number; w: number; h: number },
  centerAlpha: number,
  midStop: number,
  midAlpha: number,
  ground: RGB
) {
  const cx = (sa.x + sa.w / 2) * W
  const cy = (sa.y + sa.h / 2) * H
  const rx = Math.max(sa.w * W * 1.05, W * 0.44, Math.min(W, H) * 0.5)
  const ry = Math.max(sa.h * H * 0.95, H * 0.52, Math.min(W, H) * 0.44)
  const k = ry / rx || 1
  ctx.save()
  ctx.translate(cx, cy)
  ctx.scale(1, k)
  const g = ctx.createRadialGradient(0, 0, 0, 0, 0, rx)
  const [gr, gg, gb] = ground
  g.addColorStop(0, `rgba(${gr},${gg},${gb},${centerAlpha})`)
  g.addColorStop(midStop, `rgba(${gr},${gg},${gb},${midAlpha})`)
  g.addColorStop(1, `rgba(${gr},${gg},${gb},0)`)
  ctx.fillStyle = g
  /* Upstream asks for 4W x 4H here; in the translated/scaled space the canvas
     occupies exactly this rect, so request only that. Measured as a wash — the
     2D rasteriser already clips the oversized fill — but it stops the intent
     from reading as "fill sixteen screens of gradient". */
  ctx.fillRect(-cx, -cy / k, W, H / k)
  ctx.restore()
}

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * CopperplateHatch — "Engraver's Light": waves of candle-gold light radiate from a
 * focal point, igniting each burin stroke as they pass through a fixed three-angle
 * crosshatch field. One `<canvas>` + one requestAnimationFrame loop — no per-frame
 * React state, no WebGL. Colors are a FIXED palette (opaque `#0a0b10` ground painted
 * first every frame) — a deliberate committed-dark design, not resolved from theme
 * tokens; `accent` may override the glint. Pauses when scrolled offscreen/hidden and
 * renders a single rich still frame under reduced motion or `speed<=0`.
 */
function CopperplateHatchBase({
  density = 1,
  coverage = 1,
  intensity = 1,
  speed = 1,
  focalPoint,
  safeArea = DEFAULT_SAFE_AREA,
  accent,
  seed = 1,
  pauseWhenHidden = true,
  interactive = false,
  reducedMotion,
  className,
  style,
  children,
  ...props
}: CopperplateHatchProps) {
  const uid = React.useId().replace(/[^a-zA-Z0-9]/g, '')
  const cls = `mk-cph-${uid}`
  const wrapRef = React.useRef<HTMLDivElement | null>(null)
  const canvasRef = React.useRef<HTMLCanvasElement | null>(null)
  const pointerRef = React.useRef<{ x: number; y: number; on: boolean }>({
    x: 0,
    y: 0,
    on: false,
  })

  const systemReduced = useReducedMotion()
  // The system preference isn't known at SSR, so ignore it until after mount —
  // server and first client render agree on data-motion (no hydration mismatch).
  const [hydrated, setHydrated] = React.useState(false)
  React.useEffect(() => setHydrated(true), [])
  const staticMode =
    reducedMotion === true || speed <= 0 || (hydrated && systemReduced)
  const onScreen = useVisibilityPause(wrapRef, { threshold: 0.01 })
  const paused = pauseWhenHidden && !onScreen
  const stalled = staticMode
  const animate = !staticMode && !paused

  // Keyed by VALUE (not reference) so an inline focalPoint array doesn't force a
  // rebuild every render.
  const focalKey = Array.isArray(focalPoint)
    ? focalPoint.map((f) => `${f.x},${f.y}`).join('|')
    : focalPoint
      ? `${focalPoint.x},${focalPoint.y}`
      : ''
  const model = React.useMemo(
    () => ({ seed, density, focalPoint, coverage }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [seed, density, coverage, focalKey]
  )

  // Live prop mirror so the rAF loop reads fresh values without re-subscribing.
  const paramsRef = React.useRef({
    intensity,
    speed,
    safeArea,
    accent,
    interactive,
  })
  paramsRef.current = { intensity, speed, safeArea, accent, interactive }

  // Focal light follows the pointer — the aria-hidden layer is pointer-events:none,
  // so listen on the actual interactive ancestor (the component root).
  React.useEffect(() => {
    const el = wrapRef.current
    if (!el || !interactive || staticMode) return
    const host = el.parentElement ?? el
    const onMove = (ev: PointerEvent) => {
      const r = el.getBoundingClientRect()
      if (!r.width || !r.height) return
      pointerRef.current = {
        x: ev.clientX - r.left,
        y: ev.clientY - r.top,
        on: true,
      }
    }
    const onLeave = () => {
      pointerRef.current.on = false
    }
    host.addEventListener('pointermove', onMove)
    host.addEventListener('pointerleave', onLeave)
    return () => {
      host.removeEventListener('pointermove', onMove)
      host.removeEventListener('pointerleave', onLeave)
    }
  }, [interactive, staticMode])

  React.useEffect(() => {
    const canvas = canvasRef.current
    const wrap = wrapRef.current
    if (!canvas || !wrap) return
    const ctx = canvas.getContext('2d')
    // jsdom / unsupported contexts return null — render markup, skip drawing.
    if (!ctx) return

    let width = 0
    let height = 0
    let ground: RGB = FALLBACK_GROUND
    let ink: RGB = FALLBACK_INK
    const readTheme = () => {
      ground = readToken(wrap, '--background', FALLBACK_GROUND)
      ink = readToken(wrap, '--muted-foreground', FALLBACK_INK)
    }
    readTheme()
    let state: CopperState = {
      strokes: [],
      half: 11,
      fx: 0,
      fy: 0,
      tx: 0,
      ty: 0,
    }
    const measure = () => {
      const dpr = Math.min(
        2,
        typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1
      )
      width = wrap.clientWidth || 1
      height = wrap.clientHeight || 1
      canvas.width = Math.max(1, Math.round(width * dpr))
      canvas.height = Math.max(1, Math.round(height * dpr))
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      state = buildCopperState(
        model.seed,
        model.density,
        model.focalPoint,
        width,
        height,
        model.coverage
      )
    }
    measure()

    const draw = (timeSec: number) => {
      const {
        intensity: inten,
        speed: spd0,
        accent: accentProp,
        interactive: inter,
      } = paramsRef.current

      const W = width
      const H = height
      // Opaque ground FIRST, in the page's own background colour so the plate
      // sits flush with the section around it instead of as a black slab.
      ctx.fillStyle = `rgb(${ground[0]}, ${ground[1]}, ${ground[2]})`
      ctx.fillRect(0, 0, W, H)
      if (!state.strokes.length) return

      const glow = clamp(inten, 0, 1.4)
      const spd = clamp(spd0, 0, 4)

      const pointer = pointerRef.current
      if (inter && pointer.on) {
        state.tx = pointer.x
        state.ty = pointer.y
      }
      if (stalled) {
        state.fx = state.tx
        state.fy = state.ty
      } else {
        state.fx += (state.tx - state.fx) * 0.03
        state.fy += (state.ty - state.fy) * 0.03
      }

      const t = timeSec * spd
      const maxR = Math.hypot(W, H) * 0.75
      const span = maxR + 300
      const w1 = ((t * 130) % span) - 100
      const w2 = ((t * 130 + span / 2) % span) - 100

      const accentRGB = parseAccentColor(accentProp, GOLD)

      /* The light's tonal reach. Upstream's fixed 260px reads as a candle-pool
         on a wide desktop plate but floodlights a phone-sized canvas edge to
         edge, so it scales down with the plate's diagonal instead. */
      const sigma = clamp(Math.hypot(W, H) * 0.17, 120, 260)
      const sig2 = 2 * sigma * sigma

      for (const s of state.strokes) {
        const dx = s.x - state.fx
        const dy = s.y - state.fy
        const d = Math.hypot(dx, dy)
        const g = Math.exp(-(d * d) / sig2)
        const tonal = s.base + g * 1.1
        if (tonal < s.th) continue
        const a1 = Math.exp(-((d - w1) ** 2) / (2 * 70 * 70))
        const a2 = Math.exp(-((d - w2) ** 2) / (2 * 70 * 70))
        const glint = Math.max(a1, a2 * 0.7)
        const ang = ANGLE_RAD[s.li]!
        const dxs = Math.cos(ang) * state.half
        const dys = Math.sin(ang) * state.half
        const base = 0.1 + 0.1 * Math.sin(t * 0.5 + s.li * 2.1)

        if (glint > 0.05) {
          // Literal brightening: red channel fixed, green/blue lift toward a pale
          // tint as the wavefront passes.
          const gr = accentRGB[0]
          const gg = Math.min(255, accentRGB[1] + 40 * glint)
          const gb = Math.min(255, accentRGB[2] + 80 * glint)
          ctx.strokeStyle = `rgba(${gr | 0}, ${gg | 0}, ${gb | 0}, ${clamp(
            (base + glint * 0.85) * glow,
            0,
            0.95
          )})`
        } else {
          ctx.strokeStyle = rgba(ink, clamp((base + g * 0.14) * glow, 0, 1))
        }
        ctx.lineWidth = 1.1 + glint * 1.2
        ctx.beginPath()
        ctx.moveTo(s.x - dxs, s.y - dys)
        ctx.lineTo(s.x + dxs, s.y + dys)
        ctx.stroke()
      }

      // Focal halo — breathes with a slow pulse. The gradient is fully
      // transparent past HALO_R, so filling the whole canvas (as upstream does)
      // costs a screen of blending to paint nothing. This frame is fill-rate
      // bound, so fill only the disc's bounding box — pixel-identical, cheaper.
      // Sized to the light's pool so a small plate gets a small halo.
      const HALO_R = sigma * 1.15
      const hg = ctx.createRadialGradient(
        state.fx,
        state.fy,
        0,
        state.fx,
        state.fy,
        HALO_R
      )
      const pulse = 0.1 + 0.04 * Math.sin(t * 1.4)
      hg.addColorStop(0, rgba(accentRGB, pulse * glow))
      hg.addColorStop(1, rgba(accentRGB, 0))
      ctx.fillStyle = hg
      ctx.fillRect(state.fx - HALO_R, state.fy - HALO_R, HALO_R * 2, HALO_R * 2)

      // Quiet zone — drawn LAST so the plate dims under the copy.
      paintQuietZone(
        ctx,
        W,
        H,
        paramsRef.current.safeArea,
        0.82,
        0.6,
        0.42,
        ground
      )
    }

    let raf = 0
    let startTime = 0
    const loop = (now: number) => {
      if (!startTime) startTime = now
      draw((now - startTime) / 1000)
      raf = requestAnimationFrame(loop)
    }

    if (animate) raf = requestAnimationFrame(loop)
    else draw(STILL_SECONDS)

    const ro =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => {
            measure()
            if (!animate) draw(STILL_SECONDS)
          })
        : null
    ro?.observe(wrap)

    // next-themes flips a class on <html>; re-read the tokens and repaint so a
    // still frame does not keep the previous theme's ground.
    const mo = new MutationObserver(() => {
      readTheme()
      if (!animate) draw(STILL_SECONDS)
    })
    mo.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class', 'style'],
    })

    return () => {
      if (raf) cancelAnimationFrame(raf)
      ro?.disconnect()
      mo.disconnect()
    }
  }, [model, animate, staticMode, stalled])

  const css = `
.${cls} { position: absolute; inset: 0; background: hsl(var(--background)); }
.${cls} .mk-cph-canvas { display: block; width: 100%; height: 100%; }
.${cls} .mk-cph-fallback { display: none; }
@media (forced-colors: active) {
  .${cls} .mk-cph-canvas { display: none; }
  .${cls} .mk-cph-fallback {
    display: block; position: absolute; inset: 7%;
    border: 1px solid CanvasText; border-radius: 14px; background: Canvas;
  }
}`.trim()

  return (
    <div
      className={cn(
        'relative isolate overflow-hidden bg-background',
        className
      )}
      style={style}
      {...props}
    >
      <div
        ref={wrapRef}
        aria-hidden="true"
        data-paused={paused ? 'true' : 'false'}
        data-motion={staticMode ? 'static' : 'animated'}
        className={cn(
          'pointer-events-none absolute inset-0 overflow-hidden',
          cls
        )}
      >
        <canvas ref={canvasRef} className="mk-cph-canvas" />
        <div className="mk-cph-fallback" />
        <style dangerouslySetInnerHTML={{ __html: css }} />
      </div>

      {/* Readability comes solely from the canvas-painted quiet zone (drawn last
          each frame). No DOM blur layer — a backdrop filter on top of it stacks
          into a visible dark ellipse. */}
      {children != null ? (
        <div className="relative z-10">{children}</div>
      ) : null}
    </div>
  )
}

export function CopperplateHatch(props: CopperplateHatchProps) {
  return <CopperplateHatchBase {...props} />
}

export default CopperplateHatch
